import axios from "axios";
import frontendConfig from "./helpers/frontendConfig";
import Vue from "vue";
import Vuex from "vuex";
import "es6-promise/auto";
import eventBus from "./EventBus";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import CryptoJS from 'crypto-js';

// Mixins
import helper from "./components/mixins/helper";
import cartByRoomMix from "./components/mixins/shoppingCart/prices/CartByRoom";
import photoParkHelper from "./components/mixins/gallery/PhotoParkHelper";
import albumPromotionCartHelper from "./helpers/albumPromotionCartHelper";
import albumExpirationHelper from "./helpers/albumExpirationHelper";
import { normalizeMojibake } from "./helpers/textEncodingHelper";
import { GetFreeAlbumsConfiguration as FetchFreeAlbumsConfiguration } from "./services/freeAlbumsService";

// importamos los modulos
import auth from '@store_modules/auth.js';
import timer from "@store_modules/timer";
import individualSales from "@store_modules/cart/AddCartByIndividual.js";

import i18n from "./i18n";
// plugin a agregar de vuex-persistedstate mantener vivas las propiedades
import VuexPersistence from "vuex-persist";
// Importamos los modelos
import Product from "./models/Product";
import SaleService from "./models/SaleService";
import { UnlockTargetEnum } from "./models/request/UnlockMediaRequest";
import {
    HasQuotingService,
    NormalizeAlbumCollections,
    NormalizeMediaResponse,
    NormalizeParkRedemption,
} from "./helpers/albumContractAdapter";
import {
    BuildCartKey,
    BuildMediaTagEntries,
    CanAddVideoByPromotion,
    FindCartKeyByAlbumKey,
    GetAlbumKeyId,
    IsCartItemForAlbum,
    NormalizeCartTags,
    ResolveVideoQuotationService,
} from "./helpers/cartContractHelper";

Vue.use(Vuex);
// Clave secreta para la encriptación
const SECRET_KEY = frontendConfig.storageKey;
// Versión inicial
const CURRENT_VERSION = '1.1.5';
const SESSION_PARKS_KEY = "parksItems";
let parksRequestInFlight = null;
let eventConfigurationRequestInFlight = null;
let eventConfigurationCache = null;
let freeAlbumsConfigurationRequestInFlight = null;

const IsAlbumCommerciallyExpiredForCart = (album, park = null) => {
    return albumExpirationHelper.IsAlbumCommerciallyExpired({
        album,
        park: park || album,
    });
};

/**
 * Returns the park identifier from an album item or a park-catalog entry.
 *
 * @param {Object} item Album, nested park, or catalog item.
 * @returns {number|null} Normalized park identifier.
 */
const GetQuoteParkId = (item) => {
    const value = Number(item?.Park?.Id ?? item?.ParkId ?? item?.Id);
    return Number.isInteger(value) && value > 0 ? value : null;
};

/**
 * Adds the catalog SivexPrefix only to the transient quotation payload.
 *
 * Album API responses no longer carry commercial SIVEX configuration. The
 * catalog returned by GetParks is the single source used while quoting, and the
 * original album objects remain unchanged in Vuex.
 *
 * @param {Array<Object>} parks Albums or park entries included in the quote.
 * @param {Array<Object>} catalog Park catalog returned by GetParks.
 * @returns {Array<Object>} Quote-only park entries with SivexPrefix populated.
 */
const BuildQuoteParks = (parks, catalog) => {
    if (!Array.isArray(parks)) {
        return [];
    }

    const catalogById = new Map(
        (Array.isArray(catalog) ? catalog : [])
            .map((park) => [GetQuoteParkId(park), park])
            .filter(([parkId]) => parkId !== null)
    );

    return parks.map((item) => {
        const parkId = GetQuoteParkId(item);
        const catalogPark = catalogById.get(parkId);
        const sivexPrefix = String(catalogPark?.SivexPrefix || "").trim();

        if (!sivexPrefix) {
            throw new Error(
                `The GetParks catalog does not contain a SivexPrefix for ParkId ${parkId}.`
            );
        }

        if (item?.Park && typeof item.Park === "object") {
            return {
                ...item,
                Park: {
                    ...item.Park,
                    SivexPrefix: sivexPrefix,
                },
            };
        }

        return {
            ...item,
            SivexPrefix: sivexPrefix,
        };
    });
};

export default new Vuex.Store({
    modules: { auth, timer, individualSales },
    // aquí agregamos el plugin
    plugins: [
        new VuexPersistence({
            key: "store",
            storage: window.localStorage,
            reducer(val) {
                const persistedState = {
                    ...val,
                };

                /*
                 * The free-albums configuration is an in-memory cache.
                 * It must not be stored in localStorage.
                 */
                delete persistedState.freeAlbumsConfiguration;
                delete persistedState.freeAlbumsConfigurationLoaded;

                return persistedState;
            },
            restoreState(key, storage) {
                const encryptedData = storage.getItem(key);
                if (encryptedData) {
                    try {
                        const bytes = CryptoJS.AES.decrypt(
                            encryptedData,
                            SECRET_KEY
                        );
                        const decryptedData = JSON.parse(
                            bytes.toString(CryptoJS.enc.Utf8)
                        );

                        // Comparar la versión del estado almacenado con la actual
                        if (decryptedData.version !== CURRENT_VERSION) {
                            // Resetea el estado si la versión es diferente
                            return undefined;
                        }

                        // Evitamos restaurar estados transitorios de UI.
                        decryptedData.loading = false;

                        /*
                        * The free-albums configuration must be loaded again
                        * when the browser application starts a new lifecycle.
                        */
                        decryptedData.freeAlbumsConfiguration = [];
                        decryptedData.freeAlbumsConfigurationLoaded = false;

                        return decryptedData;
                    } catch (error) {
                        return undefined;
                    }
                }
                return undefined;
            },
            saveState(key, state, storage) {
                // Añadir versión al estado antes de guardar
                const stateWithVersion = {
                    ...state,
                    version: CURRENT_VERSION, // Agrega la versión actual al estado
                };

                // Cifrar y guardar el estado en localStorage
                const encryptedData = CryptoJS.AES.encrypt(
                    JSON.stringify(stateWithVersion),
                    SECRET_KEY
                ).toString();
                storage.setItem(key, encryptedData);
            },
        }).plugin,
    ],
    state: {
        Language: "",
        translate: [],
        IPAddress: document
            .querySelector('meta[name="address"]')
            .getAttribute("content"),
        componentes: {
            toolbarPrice: 0,
            modalAddNewAlbum: false,
            displayDownloadBox: false,
            album: "",
            shoppingCart: "",
            modalLogin: true,
            modalCloseSession: true,
            modalSessionExpired: false,
        },
        randomKey: 0,
        typeCodeTab: 0,
        tabDownloadCode: false,
        tabTags: false,
        tabReservations: false,
        tabFreeGallery: true,
        newKeyComponent: 0,
        userLoggedIn: true,
        isGuest: true,
        email: "",
        loading: false,
        needReloadAlbumes: false,
        codes: {}, //Propiedad para almacenar todos los codigos consultados y guardados en localstorage
        shoppingCart: {}, //Propiedad para alamacenar todoslos productor seleccionados y alamacenados en storage para venta
        shoppingCartAmount: 0,
        CurrentPrice: {},
        CartTypes: {},
        Currency: "",
        Currencies: [],
        albumes: [],
        AlbumPromotionConfig: {},
        freeAlbumsConfiguration: [],
        freeAlbumsConfigurationLoaded: false,
        Parks: [],
        Banks: {},
        SafetyPayBanks: {},
        CardTypes: [],
        zip: {
            data: [],
            compressed: 0,
            total: 0,
        },
        downloaded: {
            contentLength: 0,
            byteLength: 0,
            spinning: false,
            data: [],
            progressing: false,
            compressing: false,
            compressingZip: {
                compressed: 0,
                total: 0,
            },
            progress: 0,
            completed: false,
        },
        payment: {
            countries: [],
            states: [],
            Amount: 0,
            pax: {
                website: [],
            },
        },
    },
    mutations: {
        SET_LANGUAGE(state, data) {
            state.Language = data;
        },
        SET_UPDATE_ALBUM_COMPONENT(state, data) {
            state.componentes.album = uuidv4();
        },
        SET_UPDATE_SHOPPING_CART_COMPONENT(state, data) {
            state.componentes.shoppingCart = uuidv4();
        },
        SET_LOGOUT(state) {
            state.albumes = [];
            state.AlbumPromotionConfig = {};
            state.freeAlbumsConfiguration = [];
            state.freeAlbumsConfigurationLoaded = false;
            state.shoppingCart = {};
            state.shoppingCartAmount = 0;
            state.userLoggedIn = true;
            state.isGuest = true;
            state.loading = false;
            state.componentes.modalLogin = false;
            state.componentes.modalCloseSession = true;
            state.componentes.modalSessionExpired = false;
            state.typeCodeTab = null;
            state.tabDownloadCode = false;
            state.tabTags = false;
            state.tabReservations = false;
            state.email = "";
            state.componentes.displayDownloadBox = false;
            state.Parks = [];
            state.auth.user = {};
            state.payment.Amount = 0;
        },
        SET_TRANSLATE(state, data) {
            state.translate = data;
        },
        SET_ALBUM(state, data) {
            state.albumes = state.albumes.length
                ? data.concat(state.albumes)
                : data;
        },
        SET_ALBUM_PROMOTION_CONFIG(state, data) {
            state.AlbumPromotionConfig = data || {};
        },
        /**
         * Stores the free-albums configuration in the Vuex in-memory cache.
         *
         * @param {Object} state Vuex state.
         * @param {Array<Object>} data Free-albums configuration entries.
         * @returns {void}
         */
        SET_FREE_ALBUMS_CONFIGURATION(state, data) {
            state.freeAlbumsConfiguration = Array.isArray(data)
                ? data : [];

            state.freeAlbumsConfigurationLoaded = true;
        },

        /**
         * Clears the free-albums configuration cache.
         *
         * @param {Object} state Vuex state.
         * @returns {void}
         */
        RESET_FREE_ALBUMS_CONFIGURATION(state) {
            state.freeAlbumsConfiguration = [];
            state.freeAlbumsConfigurationLoaded = false;
        },
        REFRESH_ALBUM(state, { idx, data }) {
            state.albumes[idx] = data;
        },
        DELETE_ALBUM(state, data) {
            state.albumes.splice(data, 1);
        },
        SET_SHOPPING_CART_AMOUNT(state, data) {
            state.shoppingCartAmount = data;
        },
        SET_LOADING(state, data) {
            state.loading = data;
        },
        SET_PARK_REDEMPTION(state, { idx, data }) {
            const redemption = NormalizeParkRedemption(data);
            Vue.set(state.albumes[idx], "parkRedemption", redemption);

            const selectedParks = redemption?.SelectedParks || [];
            (state.albumes[idx].park || []).forEach((item) => {
                const parkId = Number(item?.Park?.Id);
                Vue.set(
                    item,
                    "Unlocked",
                    Boolean(item?.BaseUnlocked) || selectedParks.includes(parkId)
                );
            });
        },
        SET_USER_LOGGED_IN(state, data) {
            state.userLoggedIn = data;
        },
        SET_USER_GUEST(state, data) {
            state.isGuest = data;
        },
        SET_MODAL_LOGIN(state, data) {
            state.componentes.modalLogin = data;
        },
        SET_MODAL_CLOSE_SESSION(state, data) {
            state.componentes.modalCloseSession = data;
        },
        SET_MODAL_SESSION_EXPIRED(state, data) {
            state.componentes.modalSessionExpired = data;
        },
        SET_USER_EMAIL(state, data) {
            state.email = data;
        },
        SET_AUTH_GUEST(state) {
            state.isGuest = true;
            state.userLoggedIn = true;
            state.componentes.modalLogin = false;
        },
        SET_MODAL_ADD_ALBUM(state, data) {
            state.componentes.modalAddNewAlbum = data;
        },
        SET_TYPE_CODE_TAB(state, data) {
            state.typeCodeTab = data;
        },
        SET_DOWNLOAD_CODE_TAB(state, data) {
            state.tabDownloadCode = data;
        },
        SET_TAGS_TAB(state, data) {
            state.tabTags = data;
        },
        SET_RESERVATIONS_TAB(state, data) {
            state.tabReservations = data;
        },
        CREATE_NEW_KEY_COMPONENT(state, data) {
            state.newKeyComponent = data;
        },
        SET_SHOPPING_CART(state, data) {
            state.shoppingCart = data;
        },
        SET_ALBUMES(state, data) {},
        SET_ALBUMES_FREE(state, room) {
            state.albumes.forEach(function callback(item, idx, array) {
                if (item.code == room) {
                    state.albumes[idx].park.forEach(function callback(
                        item,
                        idx2,
                        array
                    ) {
                        if (item.IsHotel) {
                            state.albumes[idx].park[idx2].IsAdded = false;
                        }
                    });
                }
            });
        },
        /**
         * We get all the tags in the shopping cart, then we loop through them and delete them from the
         * albumes array.
         * @param state - The state object
         */
        SET_REMOVE_TAGS_PURCHASED_ALBUMS(state) {
            // Obtenemos todos los brazaletes que se encuentran en el carrito
            const tags = Object.values(state.shoppingCart)
                .filter((item) => item.type === 1)
                .map((tag) => {
                    return tag.Tags;
                })
                .flat();

            // Recorremos todos los brazaletes
            tags.map(function (data) {
                // Recuperamos la posicion del album
                const idx = state.albumes.findIndex(
                    (item) =>
                        item.code == data.tag &&
                        item.VisitDate == data.visitedOn
                );

                // Eliminamos el brazalete despues que se realizo la compra
                delete state.albumes[idx];
            });

            /*
             *Verificamos si se compraron todos los tags o quedaron para comprar
             *para que podamos deshabilitar la pestña de TAGS
             */
            const hasSomeTag = state.albumes.some((item) => item.type === 1);
            if (!hasSomeTag) {
                state.tabTags = false;
            }
        },
        SET_CLEAR_SHOPPING_CART(state) {
            state.shoppingCart = {};
            state.payment.Amount = 0;
            state.shoppingCartAmount = 0;
        },
        /**
         * It clears the shopping cart.
         * @param state - The state object.
         */
        SET_CLEAR_ALBUMES(state) {
            let videosIdList = Object.values(state.shoppingCart)
                .filter((item) => item.type === 3)
                .map((item) => {
                    state.albumes.map((album) => {
                        if (item.AlbumUuid === album.uuid) {
                            album.park.map((park) => (park.IsAdded = true));
                        }
                    });
                });

            let albumsBkp = [];
            state.albumes.map((album, idx) => {
                // Reservations | Regular Parks
                if ((album.Type == 2) | (album.Type == 1)) {
                    // Verificamos si de este album hay alguno agregado al carrito para poder eliminar el album
                    let addedParks = album.park.some((park) => park.IsAdded);

                    if (!addedParks) {
                        albumsBkp.push(album);
                    }
                }
                // Download Codes
                else if (album.Type == 0) {
                    album.park.map((park) => {
                        delete park.Amount;
                        park.IsAdded = false;
                    });

                    albumsBkp.push(album);
                }
            });

            state.albumes = albumsBkp;

            // Verificamos si hay reservas o Codigos con tags para ver si deshabilitamos sus Tabs
            const hasReserves = state.albumes.some((park) => park.Type == 2);
            const hasTags = state.albumes.some((park) => park.Type == 1);

            state.tabTags = hasTags;
            state.tabReservations = hasReserves;

            // Habilitamos el tab de codigos de descarga
            state.typeCodeTab = 0;
            state.tabDownloadCode = true;

            // Limpiamos las propiedades del carrito
            state.shoppingCart = {};
            state.payment.Amount = 0;
            state.shoppingCartAmount = 0;
        },
        /**
         * It filters the array of albums by the type of album (0 = deleted) and then removes the album
         * from the array
         * @param state - The current state of the store.
         */
        SET_DELETE_DOWNLOAD_CODES_IN_ALBUMS(state) {
            let albums = state.albumes.filter((album) => album.Type !== 0);
            state.albumes = albums;
        },
        SET_CODES(state, data) {
            state.codes = data;
        },
        SET_STATUS_RELOAD_ALBUMES(state, status) {
            state.needReloadAlbumes = status;
        },
        SET_SPINNING_MULTIMEDIA(state, data) {
            state.downloaded.spinning = data;
        },
        SET_CURRENCIES(state, data) {
            state.Currencies = data;
        },
        SET_CURRENCY(state, data) {
            state.Currency = data;
        },
        SET_PROGRESSING_MULTIMEDIA(state, data) {
            state.downloaded.progressing = data;
        },
        SET_COMPRESSING_MULTIMEDIA(state, data) {
            state.downloaded.compressing = data;
        },
        SET_NEW_DATA_DOWNLOADED(state, { idx, data }) {
            state.downloaded.data[idx].byteLength = data.byteLength;
            state.downloaded.data[idx].porcent = data.porcent;
        },
        SET_NEW_DATA_ZIP(state, { idx, progress }) {
            state.zip.data[idx].porcent = progress;
        },
        SET_ZIP(state, data) {
            state.zip.data = data;
        },
        SET_DOWNLOADED(state, data) {
            state.downloaded.data = data;
        },
        SET_EMPTY_DOWNLOAD(state) {
            state.downloaded.data = [];
            state.zip.compressed = 0;
            state.zip.data = [];
            state.zip.total = 0;
            state.downloaded.compressingZip.compressed = 0;
            state.downloaded.compressingZip.total = 0;
        },
        SET_DOWNLOAD_BOX_STATUS(state, show) {
            state.componentes.displayDownloadBox = show;
        },
        SET_DOWNLOADED_PROGRESS(state, data) {
            state.downloaded.progress = data;
        },
        SET_DOWNLOADED_CONTENT_LENGTH(state, { idx, data }) {
            state.downloaded.data[idx].contentLength = data.contentLength;
            state.downloaded.data[idx].sizeFormat = data.formatBytes;
        },
        SET_DOWNLOADED_URL(state, { idx, data }) {
            state.downloaded.data[idx].url = data;
        },
        SET_DOWNLOADED_STATE(state, { idx, data }) {
            state.downloaded.data[idx].state = data;
        },
        SET_DOWNLOADED_COMPRESSINGZIP_COMPRESSED(state, compressedZipQuantity) {
            state.downloaded.compressingZip.compressed = compressedZipQuantity;
        },
        SET_DOWNLOADED_COMPRESSINGZIP_TOTAL(state, totalZip) {
            state.downloaded.compressingZip.total = totalZip;
        },
        SET_DOWNLOADED_BYTE_LENGTH(state, data) {
            state.downloaded.byteLength = data;
        },
        SET_ZIP_COMPRESSED(state, data) {
            state.zip.compressed = data;
        },
        SET_ZIP_TOTAL(state, data) {
            state.zip.total = data;
        },
        SET_COMPLETED_DOWNLOAD(state, data) {
            state.downloaded.completed = data;
        },
        SET_CONTRIES(state, data) {
            state.payment.countries = data;
        },
        SET_COUNTRY_STATES(state, data) {
            state.payment.states = data;
        },
        SET_AMOUNT(state, data) {
            state.payment.Amount = data;
        },
        SET_PAX_PAYMENT(state, data) {
            state.payment.pax.website = { ...data };
        },
        SET_PARKS(state, data) {
            state.Parks = data;
        },
        SET_EVENT_DETAILS(state, data) {
            state.EventDetails = data;
        },
        SET_BANKS(state, data) {
            state.Banks = data;
        },
        SET_SAFETY_PAY_BANKS(state, data) {
            state.SafetyPayBanks = data;
        },
        SET_CARD_TYPE_BANKS(state, data) {
            state.CardTypes = data;
        },
        SET_CURRENT_PRICE(state, data) {
            state.shoppingCart[data.ParkId].Id = data.Id;
            state.shoppingCart[data.ParkId].Amount = data.Amount;
            state.shoppingCart[data.ParkId].RateKey = data.RateKey;
            state.shoppingCart[data.ParkId].NormalIndividual =
                data.NormalIndividual;
        },
        /**
         * Stores a quoted product using its stable cart identity.
         *
         * @param {Object} state Vuex state.
         * @param {Object} payload Quoted cart product.
         * @returns {void}
         */
        setCarrito(state, payload) {
            let cartKey = payload?.CartKey;

            if (!cartKey && payload.type === 3) {
                cartKey = payload.VideoId;
            } else if (!cartKey && payload.type === 2) {
                cartKey = payload.BookId;
            } else if (!cartKey && payload.type === 0) {
                cartKey = payload.code;
            } else if (!cartKey) {
                cartKey = payload.ServiceId != null
                    ? payload.isEvent
                        ? payload.id
                        : payload.ParkId
                    : payload.ParkId;
            }

            Vue.set(state.shoppingCart, String(cartKey), {
                ...payload,
                CartKey: String(cartKey),
            });
        },
        /**
         * Removes one cart product without replacing the cart object.
         *
         * @param {Object} state Vuex state.
         * @param {string|number} cartKey Stable cart key.
         * @returns {void}
         */
        REMOVE_CART_ITEM(state, cartKey) {
            if (cartKey !== undefined && cartKey !== null) {
                Vue.delete(state.shoppingCart, String(cartKey));
            }
        },
        SET_REMOVE_ITEM_SHOPPING_CART(state, data) {
            Vue.delete(state.shoppingCart, String(data));
        },
        REMOVE_ITEM_BY_TAG_CART(state, { visitDate, parkId, serviceId = null }) {
            Object.keys(state.shoppingCart).forEach((cartKey) => {
                const item = state.shoppingCart[cartKey];
                const matchesPhoto =
                    Number(item?.type) === 1 &&
                    Number(item?.ParkId) === Number(parkId) &&
                    String(item?.VisitDate || "").slice(0, 10) ===
                        String(visitDate || "").slice(0, 10) &&
                    (item?.ServiceId || null) === (serviceId || null);
                const matchesVideo =
                    Number(item?.type) === 3 &&
                    Number(item?.VideoDetails?.parkId) === Number(parkId) &&
                    String(item?.VisitDate || "").slice(0, 10) ===
                        String(visitDate || "").slice(0, 10);

                if (matchesPhoto || matchesVideo) {
                    Vue.delete(state.shoppingCart, cartKey);
                }
            });
        },
        SET_UPDATE_ALBUM_PV(state, data) {
            state.albumes[data.idxAlbum] = data.albumData;
        },
        /**
         * Funcion para calcular de manera dinamica el precio del carrito.
         */
        RECALCULATE_CART_PAYMENT(state, itemCartDeleted) {
            state.shoppingCart.map((item) => {
                const itemUpdated = {
                    id:
                        Date.now() +
                        "-" +
                        Math.random().toString(36).substring(2, 5),
                    cantidad: item.cantidad - 1,
                    ParkId: item.ParkId,
                    ParkPrefix: helper.methods.getParkPrefix(item.ParkId),
                    type: 1,
                    TotalMedia: item.TotalMedia - itemCartDeleted.totalMedia,
                    Id: 0,
                    Amount: 0,
                    NormalIndividual: 0,
                    RateKey: "",
                };
                let newPrice = dispatch("GetNewPrice", itemUpdated);
                commit("setCarrito", newPrice);
            });
        },
    },
    actions: {
        setLanguage({ commit }, languages) {
            if (typeof languages === "string") {
                commit("SET_LANGUAGE", languages);
            } else {
                const language = supportedLanguages.find((sl) =>
                    languages.find((l) =>
                        l.split(new RegExp(sl, "gi")).length - 1 > 0 ? sl : null
                    )
                );
                commit("SET_LANGUAGE", language);
            }
        },
        updateAlbumComponent({ commit }, data) {
            commit("SET_UPDATE_ALBUM_COMPONENT");
        },
        updateShoppingCartComponent({ commit }, data) {
            commit("SET_UPDATE_SHOPPING_CART_COMPONENT");
        },
        /**
         * The setLogout function sets the logout state to true and dispatches the setEmptyDownload action.
         */
        setLogout({ commit, dispatch }) {
            /* Setting the logout state to true. */
            commit("SET_LOGOUT");
            /* Dispatching the setEmptyDownload action. */
            dispatch("setEmptyDownload");
        },
        getToken() {
            return (
                Date.now() + "-" + Math.random().toString(36).substring(2, 5)
            );
        },
        /**
         * cleanEventDetails
         */
        async cleanEventDetails({ commit }) {
            commit("SET_EVENT_DETAILS", {});
        },
        /**
         * It takes in a data object, and then sends it to the server
         * @param data
         * @returns A promise.
         */
        async getEventDetails({ commit }, data) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/GetEventDetails", data)
                    .then((res) => {
                        commit("SET_EVENT_DETAILS", res.data);
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * It takes in a data object, and then sends it to the server
         * @param data
         * @returns A promise.
         */
        async VisitorActivity({ commit }, data) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/VisitorActivity", data)
                    .then((res) => {
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        async getAlbums({ commit }, data) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/GetAlbums", data)
                    .then((res) => {
                        resolve(NormalizeAlbumCollections(res.data));
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * Gets the free-albums configuration using an in-memory Vuex cache.
         *
         * When the configuration is already loaded, the cached value is returned.
         * When another request is running, the same pending promise is returned.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} context.state Vuex state.
         * @param {Function} context.commit Vuex commit function.
         * @returns {Promise<Array<Object>>} Free-albums configuration entries.
         */
        async GetFreeAlbumsConfiguration({ state, commit }) {
            if (state.freeAlbumsConfigurationLoaded) {
                return state.freeAlbumsConfiguration;
            }

            if (freeAlbumsConfigurationRequestInFlight) {
                return freeAlbumsConfigurationRequestInFlight;
            }

            freeAlbumsConfigurationRequestInFlight =
                FetchFreeAlbumsConfiguration()
                    .then((configuration) => {
                        const normalizedConfiguration =
                            Array.isArray(configuration)
                                ? configuration
                                : [];

                        commit(
                            "SET_FREE_ALBUMS_CONFIGURATION",
                            normalizedConfiguration
                        );

                        return normalizedConfiguration;
                    })
                    .catch((error) => {
                        commit("RESET_FREE_ALBUMS_CONFIGURATION");

                        throw error;
                    })
                    .finally(() => {
                        freeAlbumsConfigurationRequestInFlight = null;
                    });

            return freeAlbumsConfigurationRequestInFlight;
        },
        /**
         * It gets the data from the API and commits it to the store
         */
        async getTranslate({ commit }) {
            try {
                const { data: data } = await axios.get("/api/GetLanguages");
                commit("SET_TRANSLATE", data);
            } catch (error) {}
        },
        /**
         * It's an async function that takes in a commit and data as parameters. It returns a promise
         * that resolves to the data returned by the axios post request
         * @param data
         * @returns The data is being returned from the API.
         */
        async Booking({ commit, state, dispatch }, data) {
            // Datos para el header de la solicitud
            const headers = {
                currency: state.Currency,
            };

            // tiempo para rechazar la solicitud
            let timeout = 4 * 60 * 1000;

            // Configuración de la solicitud
            const axiosConfig = {
                method: "post",
                url: "/api/payment/Booking",
                headers: headers,
                data: data,
                timeout: timeout,
            };

            try {
                // Realizar la solicitud Axios
                const response = await axios(axiosConfig);
                // Manejar la respuesta exitosa aquí
                return Promise.resolve(response.data);
            } catch (error) {
                if (axios.isCancel(error)) {
                    // Deshabilitar el loading
                    dispatch("setLoading", false);
                    // Manejar timeout
                    const timeoutError = new Error(
                        "La petición ha excedido el tiempo límite."
                    );
                    // Habilitar el modal de error
                    document
                        .getElementById("modalSaleError")
                        .classList.remove("hidden");
                    // Rechazar la promesa con el error de timeout
                    return Promise.reject(timeoutError);
                } else {
                    // Deshabilitar el loading
                    dispatch("setLoading", false);
                    // Rechazar la promesa con el error general
                    return Promise.reject(error);
                }
            }
        },
        async GatewayPayment({ commit, state }, data) {
            const headers = {
                currency: state.Currency,
            };

            return new Promise((resolve, reject) => {
                axios
                    .post("/api/payment/GatewayPayment", data, headers)
                    .then((res) => {
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        async SaleAdd({ commit, state }, data) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/payment/SaleAdd", data)
                    .then((res) => {
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        async RecoverySale({ commit, state }, data) {
            const headers = {
                currency: state.Currency,
            };

            return new Promise((resolve, reject) => {
                axios
                    .post("/api/payment/RecoverySale", data, headers)
                    .then((res) => {
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        async AddMediaLog({ commit }, data) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/AddMediaLog", data)
                    .then((res) => {
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * Sends a request to the unified media unlock endpoint.
         *
         * Park requests keep the album index only in Vuex so it is never sent to AWS.
         * Successful park responses refresh both selected parks and the unlocked flag.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} data Unlock request and optional local album index.
         * @returns {Promise<Object>} Unified unlock response.
         */
        async unlockMedia({ commit }, data) {
            const { idx = null, ...request } = data || {};

            return new Promise((resolve, reject) => {
                axios
                    .post("/api/UnlockMedia", request)
                    .then((res) => {
                        const response = res.data;

                        if (
                            request.UnlockTarget === UnlockTargetEnum.PARK &&
                            idx !== null
                        ) {
                            commit("SET_PARK_REDEMPTION", {
                                idx,
                                data: response.ParkRedemption,
                            });
                        }

                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * It takes in a commit and data object, and returns a promise that resolves to the data
         * returned from the API call
         * @param data
         * @returns A promise.
         */
        async getPhotos({ commit }, data) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/GetMultimedia", data)
                    .then((res) => {
                        if (data?.free === true || data?.metadataOnly) {
                            resolve(res.data);
                            return;
                        }

                        resolve(NormalizeMediaResponse(res.data));
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * Loads album tags without signing media files.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} albumKey Encrypted album context.
         * @returns {Promise<Array<string>>} Album tags returned by GetMedia.
         */
        async GetAlbumMetadata({ dispatch }, albumKey) {
            const response = await dispatch("getPhotos", {
                page: 1,
                type: 0,
                free: false,
                metadataOnly: true,
                albumKey,
            });
            return Array.isArray(response?.AlbumTags) ? response.AlbumTags : [];
        },
        /**
         * It takes in a commit and a data object, and returns a promise that resolves to the data
         * returned from the API call
         * @returns The promise is being returned.
         */
        getCompressMedia({ commit }, { data }) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/GetPhotosByZip", { data })
                    .then((res) => {
                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * The function takes in a commit object and a data object. The commit object is used to call
         * the SET_ALBUM mutation. The data object is passed to the SET_ALBUM mutation
         * @param data - The data that is being passed to the mutation.
         */
        setAlbum({ commit }, data) {
            commit("SET_ALBUM", data);
        },
        refreshAlbum({ commit }, { idx, data }) {
            commit("REFRESH_ALBUM", { idx, data });
        },
        deleteAlbum({ commit, state, dispatch }, data) {
            const parkId = state.albumes[data].ParkId;

            if (state.shoppingCart.hasOwnProperty(parkId)) {
                commit("REMOVE_CART_ITEM", parkId);
            }

            commit("DELETE_ALBUM", data);
            dispatch("addShoppingCartAmount");
            dispatch("getAmount");

            // Actualizamos el componente de Albums
            dispatch("updateAlbumComponent");
            // Actualizamos el componente del carrito
            dispatch("updateShoppingCartComponent");
        },
        /**
         * It's an async function that takes in a commit and state object, and an optional data object.
         * It then sets the state.Currency to the data object if it's not null, and then sets the
         * headers to the state.Currency. It then returns a promise that resolves to the data object
         * @param [data=null] - The data that is passed to the function.
         */
        async getBanks({ commit, state }, data = null) {
            commit("SET_CURRENCY", data);
            const iso = data != null ? data : state.Currency;
            const headers = {
                currency: iso,
            };

            return new Promise((resolve, reject) => {
                axios
                    .post(
                        "/api/payment/PaymentBanks",
                        { Amount: state.payment.Amount },
                        { headers: headers }
                    )
                    .then((res) => {
                        commit("SET_BANKS", res.data.banks);
                        commit("SET_SAFETY_PAY_BANKS", res.data.safetyPayBanks);
                        commit("SET_CARD_TYPE_BANKS", res.data.cardTypes);

                        resolve(res.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * Set Album for event TRT
         *
         * @param {*} eventDetails
         * @returns
         */
        async setAlbumTRT({ state, dispatch }, eventDetails) {
            let product = new Product({
                parkId: eventDetails.ParkId,
                parkPrefix: helper.methods.getParkPrefix(eventDetails.ParkId),
                name: eventDetails.ServiceName,
                serviceId: eventDetails.SivexId,
                hasService: true,
                date: state.competitionOn,
                saleService: new SaleService({
                    parkId: eventDetails.ParkId,
                    serviceId: eventDetails.ServiceId,
                    sivexId: eventDetails.SivexId,
                    serviceName: eventDetails.ServiceName,
                }),
                tags: ["99999"],
                isEvent: true,
                eventDetails: eventDetails,
            });
            let addProduct = await dispatch("addCartOnlyProduct", product);
            return addProduct;
        },
        /**
         * It gets all the parks from the database and commits them to the state
         * @param [data=null] - This is the data that is passed to the action.
         */
        async getParks({ commit, state }, data = null) {
            if (Array.isArray(state.Parks) && state.Parks.length) {
                return state.Parks;
            }

            let parksCached = [];
            try {
                parksCached = JSON.parse(
                    sessionStorage.getItem(SESSION_PARKS_KEY)
                );
            } catch (error) {
                sessionStorage.removeItem(SESSION_PARKS_KEY);
                parksCached = [];
            }

            if (Array.isArray(parksCached) && parksCached.length) {
                commit("SET_PARKS", parksCached);
                return parksCached;
            }

            if (parksRequestInFlight) {
                return parksRequestInFlight;
            }

            parksRequestInFlight = new Promise((resolve, reject) => {
                axios
                    .post("/api/GetAllParks", {}, {
                        timeout: 100000,
                    })
                    .then((response) => {
                        const parks = Array.isArray(response.data)
                            ? response.data
                            : [];

                        commit("SET_PARKS", parks);

                        if (parks.length) {
                            sessionStorage.setItem(
                                SESSION_PARKS_KEY,
                                JSON.stringify(parks)
                            );
                        }

                        resolve(parks);
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        parksRequestInFlight = null;
                    });
            });

            return parksRequestInFlight;
        },
        async getEventConfiguration() {
            if (Array.isArray(eventConfigurationCache)) {
                return eventConfigurationCache;
            }

            if (eventConfigurationRequestInFlight) {
                return eventConfigurationRequestInFlight;
            }

            eventConfigurationRequestInFlight = axios
                .post("/api/GetEventConfiguration", {}, {
                    timeout: 100000,
                })
                .then((response) => {
                    const data = normalizeMojibake(response.data);
                    eventConfigurationCache = Array.isArray(data) ? data : [];
                    return eventConfigurationCache;
                })
                .finally(() => {
                    eventConfigurationRequestInFlight = null;
                });

            return eventConfigurationRequestInFlight;
        },
        async getOptions({ dispatch }) {
            let fallback = [];

            try {
                const cached = normalizeMojibake(
                    JSON.parse(sessionStorage.getItem("menuItems"))
                );
                fallback = Array.isArray(cached) ? cached : [];
            } catch (error) {
                sessionStorage.removeItem("menuItems");
            }

            try {
                const options = await dispatch("getEventConfiguration");
                if (Array.isArray(options)) {
                    sessionStorage.setItem("menuItems", JSON.stringify(options));
                    return options;
                }
            } catch (error) {
                return fallback;
            }

            return fallback;
        },
        setLoading({ commit }, data) {
            commit("SET_LOADING", data);
        },
        setAlbumPromotionConfig({ commit }, data) {
            commit("SET_ALBUM_PROMOTION_CONFIG", data || {});
        },
        setModalAddAlbum({ commit }, data) {
            commit("SET_MODAL_ADD_ALBUM", data);
        },
        setTypeCodeTab({ commit }, data) {
            commit("SET_TYPE_CODE_TAB", data);
        },
        setDownloadCodeTab({ commit }, data) {
            commit("SET_DOWNLOAD_CODE_TAB", data);
        },
        setTagsTab({ commit }, data) {
            commit("SET_TAGS_TAB", data);
        },
        setReservationsTab({ commit }, data) {
            commit("SET_RESERVATIONS_TAB", data);
        },
        setUserLoggedIn({ commit }, data) {
            commit("SET_USER_LOGGED_IN", data);
        },
        setUserGuest({ commit }, data) {
            commit("SET_USER_GUEST", data);
        },
        setModalLogin({ commit }, data) {
            commit("SET_MODAL_LOGIN", data);
        },
        setModalCloseSession({ commit }, data) {
            commit("SET_MODAL_CLOSE_SESSION", data);
        },
        setModalSessionExpired({ commit }, data) {
            commit("SET_MODAL_SESSION_EXPIRED", data);
        },
        setUserEmail({ commit }, data) {
            commit("SET_USER_EMAIL", data);
        },
        setAuthGuest({ commit }) {
            commit("SET_AUTH_GUEST");
        },
        /*Accion para obtener todos los datos consultados por primera
        vez de un codigo de descarga | Brazalete | Room*/
        setShoppingCart({ commit }, data) {
            commit("SET_SHOPPING_CART", data);
        },
        /**
         * Rebuilds album cart indicators from the shopping cart.
         *
         * The cart is the single source of truth. This prevents stale red buttons
         * after a failed quote, a cart removal, or a snapshot restoration.
         *
         * @param {Object} context Vuex action context.
         * @returns {void}
         */
        SynchronizeAlbumCartState({ state }) {
            const cartItems = Object.values(state.shoppingCart || {});

            for (const album of Array.isArray(state.albumes) ? state.albumes : []) {
                for (const park of Array.isArray(album?.park) ? album.park : []) {
                    const matchingItem = cartItems.find(
                        (item) =>
                            Number(item?.type) !== 3 &&
                            IsCartItemForAlbum(item, park)
                    );
                    const isAdded = Boolean(matchingItem);

                    Vue.set(park, "IsAdded", isAdded);

                    if (Number(park?.AlbumMode) === 1) {
                        const mediaIds = Array.isArray(
                            matchingItem?.MediaCollection
                        )
                            ? matchingItem.MediaCollection.map(Number)
                            : [];

                        Vue.set(park, "MediaIds", mediaIds);
                    }
                }
            }

            eventBus.$emit("updateGalleryItems", {
                source: "SynchronizeAlbumCartState",
                update: true,
            });
        },
        setAlbumes({ commit }, data) {
            commit("SET_ALBUMES", data);
        },
        setAlbumesFree({ commit, dispatch }, room) {
            commit("SET_ALBUMES_FREE", room);
            dispatch("addShoppingCartAmount");
            dispatch("getAmount");
        },
        setCodes({ commit }, data) {
            commit("SET_CODES", data);
        },
        setStatusReloadAlbumes({ commit }, status) {
            commit("SET_STATUS_RELOAD_ALBUMES", status);
        },
        setSpinningMultimedia({ commit }, data) {
            commit("SET_SPINNING_MULTIMEDIA", data);
        },
        setProgressingMultimedia({ commit }, data) {
            commit("SET_PROGRESSING_MULTIMEDIA", data);
        },
        setCompressingMultimedia({ commit }, data) {
            commit("SET_COMPRESSING_MULTIMEDIA", data);
        },
        setNewDataZip({ commit }, { idx, data }) {
            commit("SET_NEW_DATA_ZIP", { idx, data });
        },
        setNewDataDownloaded({ commit }, { idx, data }) {
            commit("SET_NEW_DATA_DOWNLOADED", { idx, data });
        },
        setZip({ commit }, data) {
            commit("SET_ZIP", data);
        },
        setDownloaded({ commit }, data) {
            commit("SET_DOWNLOADED", data);
        },
        setEmptyDownload({ commit }) {
            commit("SET_EMPTY_DOWNLOAD");
        },
        setDownloadsBoxStatus({ commit }, show) {
            commit("SET_DOWNLOAD_BOX_STATUS", show);
        },
        setDownloadProgress({ commit }, data) {
            commit("SET_DOWNLOADED_PROGRESS", data);
        },
        setDownloadContentLength({ commit }, { idx, data }) {
            commit("SET_DOWNLOADED_CONTENT_LENGTH", { idx, data });
        },
        setDownloadUrl({ commit }, { idx, data }) {
            commit("SET_DOWNLOADED_URL", { idx, data });
        },
        setDownloadState({ commit }, { idx, data }) {
            commit("SET_DOWNLOADED_STATE", { idx, data });
        },
        setDownloadedCompressingZipCompressed(
            { commit },
            compressedZipQuantity
        ) {
            commit(
                "SET_DOWNLOADED_COMPRESSINGZIP_COMPRESSED",
                compressedZipQuantity
            );
        },
        setDownloadedCompressingZipTotal({ commit }, totalZip) {
            commit("SET_DOWNLOADED_COMPRESSINGZIP_TOTAL", totalZip);
        },
        setDownloadByteLength({ commit }, data) {
            commit("SET_DOWNLOADED_BYTE_LENGTH", data);
        },
        setZipCompressed({ commit }, data) {
            commit("SET_ZIP_COMPRESSED", data);
        },
        setZipTotal({ commit }, data) {
            commit("SET_ZIP_TOTAL", data);
        },
        setCompletedDownload({ commit }, data) {
            commit("SET_COMPLETED_DOWNLOAD", data);
        },
        setContries({ commit }, data) {
            let topCountries = helper.methods.topCountries(data);
            commit("SET_CONTRIES", topCountries);
        },
        setCountryStates({ commit }, data) {
            commit("SET_COUNTRY_STATES", data);
        },
        setCurrencies({ commit }, data) {
            commit("SET_CURRENCIES", data);
        },
        /*Acción para actualizar los precios del carrito acorde al tipo de cambio seleccionado (EUR, MXN, USD, etc) */
        /**
         * Updates the selected currency and requotes the cart as a single flow.
         *
         * @param {Object} context Vuex action context.
         * @param {Function} context.commit Vuex commit function.
         * @param {Function} context.dispatch Vuex dispatch function.
         * @param {Object} context.state Vuex state object.
         * @param {string} data Currency ISO code.
         * @returns {Promise<void>} Resolves after the cart is requoted.
         */
        async setCurrency({ commit, dispatch, state }, data) {
            const previousCurrency = state.Currency;

            try {
                dispatch("setLoading", true);
                commit("SET_CURRENCY", data);
                await dispatch("getBanks", data);
                await dispatch("updatePriceShoppingCart");
            } catch (error) {
                commit("SET_CURRENCY", previousCurrency);
                throw error;
            } finally {
                dispatch("setLoading", false);
            }
        },
        setAmount({ commit, state }) {
            // Actualizamos la propiedad
            commit("SET_AMOUNT");
        },
        setPaxPayment({ commit }, data) {
            commit("SET_PAX_PAYMENT", { ...data });
        },
        LoadNewCodes: () => {
            eventBus.$emit("LoadNewCodes");
        },
        reloadGallery: ({ commit }, data) => {
            eventBus.$emit("reloadGallery", data);
        },
        /**
         * Requests a new quotation and enriches park components from GetParks.
         *
         * Type 0 and type 2 quotations receive quote-only copies of Parks and
         * PaidParks containing the catalog SivexPrefix. Album state is not
         * mutated and the cached catalog prevents repeated network requests.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} context.state Current Vuex state.
         * @param {Function} context.dispatch Vuex dispatch function.
         * @param {Object} data Item or cart quotation payload.
         * @returns {Promise<Object>} Original item merged with quotation data.
         */
        GetNewPrice: async ({ state, dispatch }, data) => {
            const usesParkComponents = data.type == 0 || data.type == 2;
            let parkCatalog = state.Parks;

            if (usesParkComponents && (!Array.isArray(parkCatalog) || !parkCatalog.length)) {
                parkCatalog = await dispatch("getParks");
            }

            const quoteParks = BuildQuoteParks(data.Parks, parkCatalog);
            const quotePaidParks = BuildQuoteParks(data.PaidParks, parkCatalog);

            if (data.type == 3) {
                var arg = {
                    ServiceNumber: data.cantidad,
                    LocationId: data.LocationId,
                    IsOnlyHotel: false,
                    ParkId: data.ParkId,
                    type: data.type,
                    ServiceId: data.ServiceId,
                    isEvent: false,
                };
            }
            // Verificamos si es se calculara el precio de una Reserva
            else if ((data.type == 2 || data.type == 0) && data.IsOnlyHotel) {
                var arg = {
                    ServiceNumber: 1,
                    ParkId: data.ParkId,
                    Parks: quoteParks,
                    IsOnlyHotel: true,
                    type: data.type,
                    Pax: data.Pax,
                    isEvent: false,
                };
            } else if (data.type == 2 || data.type == 0) {
                const parksLength = Array.isArray(data.Parks)
                    ? data.Parks.length
                    : 0;

                const serviceNumber =
                    Number(data.ServiceNumber) ||
                    Number(data.cantidad) ||
                    parksLength ||
                    1;

                var arg = {
                    ServiceNumber: serviceNumber,
                    ParkId: data.ParkId,
                    Parks: quoteParks,
                    PaidParks: quotePaidParks,
                    PromotionApplied: data.PromotionApplied || false,
                    IsOnlyHotel: false,
                    type: data.type,
                    Pax: data.Pax,
                    isEvent: false,
                };
            } else {
                // Verificamos si es se calculara el precio de un paquete individual
                var arg = {
                    ServiceNumber: 1,
                    ParkId: data.ParkId,
                    Parks: data.Parks,
                    IsOnlyHotel: data.IsOnlyHotel,
                    type: data.type,
                    Pax: data.Pax,
                    HasService: data.hasService || false,
                    SaleService: data.saleService,
                    isEvent: data?.isEvent || false,
                    agentDetails: data?.eventDetails?.AgentDetails,
                    competitionAt: data?.date,
                    useEventDate: data?.eventDetails?.UseEventDate === true,
                };

                if (!data.hasOwnProperty("IsOnlyHotel")) {
                    arg.IsOnlyHotel = false;
                }

                if (data.ServiceId != null) {
                    arg.ServiceNumber = 1;
                    arg.ServiceId = data.ServiceId;
                    arg.uuid = data.uuid;
                }
            }

            const headers = {
                currency: state.Currency,
            };

            return new Promise((resolve, reject) => {
                axios
                    .post(
                        "/api/payment/GetNewPrice",
                        {
                            data: arg,
                        },
                        { headers: headers }
                    )
                    .then((res) => {
                        let ItemAndPrice = { ...data, ...res.data };

                        if (
                            ItemAndPrice.VideoDetails &&
                            Array.isArray(ItemAndPrice.VideoDetails.serviceId)
                        ) {
                            ItemAndPrice.VideoDetails.serviceId =
                                ItemAndPrice.ServiceId ||
                                ItemAndPrice.VideoDetails.serviceId.find((item) => item);
                        }

                        if (
                            ItemAndPrice.VideoDetails &&
                            ItemAndPrice.ServiceId
                        ) {
                            ItemAndPrice.VideoDetails.serviceId = ItemAndPrice.ServiceId;
                        }

                        resolve(ItemAndPrice);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * Update the price of each item in the shopping cart
         */
        async updatePriceShoppingCart({ commit, dispatch, state }) {
            const shoppingCart = Object.keys(state.shoppingCart);

            const promises = shoppingCart.map(async (key) => {
                const data = state.shoppingCart[key];

                if (Number(data.type) === 3) {
                    const serviceId = ResolveVideoQuotationService(
                        state.shoppingCart,
                        {
                            ParkId: data.ParkId,
                            AlbumUuid: data.AlbumUuid,
                            Album: {
                                Reservation: {
                                    BookId: data.BookId,
                                },
                                Service: {
                                    Id: data.ServiceId || null,
                                },
                                QuotingService: data.QuotingService || {},
                            },
                        }
                    );

                    if (serviceId) {
                        data.ServiceId = serviceId;
                        data.VideoDetails.serviceId = serviceId;
                    }
                }

                /*
                 * If the cart has an album promotion, block-based repricing is delegated.
                 * store.js only coordinates the flow while the promotion logic stays isolated.
                 */
                if ((data.type === 2 || data.type === 0) && data.PromotionApplied) {
                    const updatedItem = await albumPromotionCartHelper.RefreshPromotionCartItem({
                        data,
                        dispatch,
                    });

                    commit("setCarrito", updatedItem);

                    return;
                }

                const item = albumPromotionCartHelper.RefreshCartDisplayGroups(
                    await dispatch("GetNewPrice", data)
                );

                commit("setCarrito", item);
            });

            await Promise.all(promises);
            await dispatch("addShoppingCartAmount");
            await dispatch("getAmount");
            await dispatch("updateShoppingCartComponent");
        },
        /**
         * Checks the independent video promotion quantity before adding a video.
         *
         * Empty promotion responses do not block the product. Album promotion
         * rules are intentionally outside this action.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} videoAdded Quoted video product.
         * @returns {Promise<boolean>} True when the video can be added.
         */
        async applyVideoPriming({ state }, videoAdded) {
            return CanAddVideoByPromotion(
                state.shoppingCart,
                videoAdded
            );
        },
        /**
         * GetEventDetailsDynamicText is a function that takes in a request object and returns a promise
         * that resolves to the data returned from the API call.
         * @param {*} request - The request object to be sent to the API.
         * @returns {Promise<any>} - A promise that resolves to the data returned from the API call.
         */
        async getEventDetailsDynamicText(context, request) {
            return new Promise((resolve, reject) => {
                axios
                    .post("/api/GetEventDetails", request, {
                        timeout: 100000,
                    })
                    .then((response) => {
                        const { data } = response;
                        if(data?.errorType)
                            reject(data);
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * getOptionsDynamicText obtains dynamic text from sessionStorage or API.
         * Only accepts JSON object responses.
         *
         * @param {*} dispatch - Vuex dispatch function.
         * @param {*} payload - Can be params directly or { params, storeKey }.
         * @returns {Promise<Object>} - JSON object when available.
         */
        async getOptionsDynamicText({ dispatch }, payload) {
            const params = payload?.params || payload;
            const storeKey = payload?.storeKey;
            let dynamicStrings = {};

            try {
                dynamicStrings = JSON.parse(sessionStorage.getItem(storeKey));
                if (dynamicStrings && typeof dynamicStrings === "object") return dynamicStrings;
            } catch (error) {
                sessionStorage.removeItem(storeKey);
                dynamicStrings = {};
            }

            try {
                dynamicStrings = await dispatch("getEventDetailsDynamicText", params);
                sessionStorage.setItem(storeKey, JSON.stringify(dynamicStrings));

                return dynamicStrings;
            } catch (error) {
                return {};
            }
        },
        /**
         * Adds a video product using media-level tags and stable cart identity.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} product Selected video and album context.
         * @returns {Promise<Object|null>} Quoted video item or null.
         */
        async addCartByVideo({ commit, dispatch, state }, product) {
            const quotationSnapshot =
                albumPromotionCartHelper.CreateCartQuotationSnapshot({
                    state,
                });

            try {
                if (IsAlbumCommerciallyExpiredForCart(product.Album, product)) {
                    Vue.$toast.open({
                        message: i18n.t("gallery.album_expired"),
                        type: "error",
                    });
                    return null;
                }

                dispatch("setLoading", true);

                const parkId = Number(product.ParkId);
                const videoId = Number(product.MediaId || product.VideoId);
                const parkPrefix = helper.methods.getParkPrefix(parkId);
                const relatedVideos = Array.isArray(product.VideosRelated)
                    ? product.VideosRelated
                    : [];
                const selectedMedia = [product, ...relatedVideos]
                    .filter((video) => Number(video?.MediaId || video?.VideoId) > 0)
                    .filter((video, index, source) => {
                        const id = Number(video.MediaId || video.VideoId);
                        return source.findIndex(
                            (candidate) =>
                                Number(candidate.MediaId || candidate.VideoId) === id
                        ) === index;
                    });
                const mediaCollection = selectedMedia.map((video) =>
                    Number(video.MediaId || video.VideoId)
                );
                const videoTags = BuildMediaTagEntries(
                    selectedMedia,
                    parkId,
                    parkPrefix,
                    1
                );

                if (!videoTags.length) {
                    throw new Error(
                        "The selected video does not contain a valid tag and visit date."
                    );
                }

                const serviceId = ResolveVideoQuotationService(
                    state.shoppingCart,
                    product
                );
                const albumKeyId = GetAlbumKeyId(product.Album);
                const quoting = product?.Album?.QuotingService || {};
                const bookId = product?.Album?.Reservation?.BookId || null;
                const cartKey = BuildCartKey({
                    kind: "video",
                    bookId,
                    parkId,
                    mediaId: videoId,
                    serviceId,
                    albumKeyId,
                });

                const item = {
                    id: uuidv4(),
                    CartKey: cartKey,
                    AlbumKeyId: albumKeyId,
                    AlbumUuid: product.AlbumUuid,
                    cantidad: 1,
                    ParkId: parkId,
                    ParkPrefix: parkPrefix,
                    BookId: bookId,
                    Room: product?.Album?.Reservation?.Room || null,
                    Tags: NormalizeCartTags(product.Tags),
                    VisitDate: String(product.VisitDate || "").slice(0, 10),
                    VideoId: videoId,
                    MediaId: videoId,
                    LocationId: product.LocationId,
                    VideoDetails: {
                        serviceId,
                        serviceType: 1,
                        parkId,
                        tags: videoTags,
                        mediaCollection,
                    },
                    type: 3,
                    TotalMedia: mediaCollection.length,
                    Id: 0,
                    date: moment().format("YYYY-MM-DD"),
                    Amount: 0,
                    NormalIndividual: 0,
                    RateKey: "",
                    QuotingService: quoting,
                };

                if (serviceId) {
                    item.ServiceId = serviceId;
                }

                const pricedItem = await dispatch("GetNewPrice", item);

                if (!pricedItem?.RateKey || pricedItem?.Id === undefined) {
                    throw new Error("The video quotation response is incomplete.");
                }

                pricedItem.CartKey = cartKey;
                pricedItem.AlbumKeyId = albumKeyId;
                pricedItem.AlbumUuid = product.AlbumUuid;
                pricedItem.BookId = bookId;
                pricedItem.QuotingService = quoting;
                pricedItem.VideoDetails = helper.methods.groupServices({
                    ...item.VideoDetails,
                    serviceId: pricedItem.ServiceId || serviceId,
                });

                const hasPromo = await dispatch(
                    "applyVideoPriming",
                    pricedItem
                );

                if (!hasPromo) {
                    Vue.set(product, "IsAdded", false);
                    Vue.$toast.open({
                        message: i18n.t("messages.album_add_promotion_info"),
                        type: "info",
                    });
                    return null;
                }

                commit("setCarrito", pricedItem);
                Vue.set(product, "IsAdded", true);

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_add_successfully"),
                    type: "success",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");

                return pricedItem;
            } catch (error) {
                albumPromotionCartHelper.RestoreCartQuotationSnapshot({
                    state,
                    snapshot: quotationSnapshot,
                });
                Vue.set(product, "IsAdded", false);
                await dispatch("SynchronizeAlbumCartState");

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_add"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
                return null;
            } finally {
                dispatch("setLoading", false);
            }
        },
        /**
         * Synchronizes a photo-album cart item after a park selection changes.
         *
         * The existing promotion helper remains the single source of truth for
         * reservation and download-code photo packages. This wrapper intentionally
         * does not introduce or change promotion rules.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} payload Synchronization payload.
         * @param {number} payload.idx Album index.
         * @param {number} payload.sourceType Search source type.
         * @param {Object} payload.triggerPark Park that triggered the change.
         * @returns {Promise<Object|null>} Updated cart item or null.
         */
        async syncAlbumPromotionCartItem(
            { commit, dispatch, state },
            { idx, sourceType, triggerPark }
        ) {
            return albumPromotionCartHelper.SyncAlbumPromotionCartItem({
                commit,
                dispatch,
                state,
                idx,
                sourceType,
                triggerPark,
            });
        },

        /**
         * Adds a park album selected from an existing download-code result.
         *
         * Quotation-service albums keep their independent quotation. Regular photo
         * albums delegate to the existing promotion synchronizer without changing
         * any promotion business rule.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} payload Selection payload.
         * @param {Object} payload.park Selected album experience.
         * @param {number} payload.idx Album index.
         * @returns {Promise<void>}
         */
        async addCartByDownloadCode(
            { dispatch, state },
            { park, idx }
        ) {
            const quotationSnapshot =
                albumPromotionCartHelper.CreateCartQuotationSnapshot({
                    state,
                    idx,
                });

            try {
                if (
                    IsAlbumCommerciallyExpiredForCart(
                        state.albumes[idx],
                        park
                    )
                ) {
                    Vue.$toast.open({
                        message: i18n.t("gallery.album_expired"),
                        type: "error",
                    });
                    return;
                }

                await dispatch("setLoading", true);

                if (HasQuotingService(park.QuotingService)) {
                    const uuid = uuidv4();
                    Vue.set(park, "uuid", uuid);

                    await dispatch("addCartByServiceId", {
                        park,
                        tag: uuid,
                        reservation: true,
                    });

                    return;
                }

                albumPromotionCartHelper.MarkAlbumSelection({
                    park,
                    album: state.albumes[idx],
                });

                await dispatch("syncAlbumPromotionCartItem", {
                    idx,
                    sourceType: 0,
                    triggerPark: park,
                });

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_add_successfully"),
                    type: "success",
                });

                await dispatch("updateAlbumComponent");
                await dispatch("updateShoppingCartComponent");
            } catch (error) {
                albumPromotionCartHelper.RestoreCartQuotationSnapshot({
                    state,
                    snapshot: quotationSnapshot,
                });

                await dispatch("SynchronizeAlbumCartState");

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_add"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                dispatch("setLoading", false);
            }
        },
        /**
         * Adds a quotation-service album to the shopping cart.
         * Dynamic events such as Bravest use the same quotation-service flow,
         * but preserve all searched tags and mark the item as an event.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} payload Cart payload.
         * @returns {Promise<void>}
         */
        async addCartByServiceId(
            { commit, dispatch, state },
            {
                park,
                tag,
                tags = [],
                eventDetails = null,
                reservation = false,
            }
        ) {
            const quotationSnapshot = albumPromotionCartHelper.CreateCartQuotationSnapshot({
                state,
            });

            try {
                if (IsAlbumCommerciallyExpiredForCart(park)) {
                    Vue.$toast.open({
                        message: i18n.t("gallery.album_expired"),
                        type: "error",
                    });
                    return;
                }

                dispatch("setLoading", true);

                const parkId = park.Park.Id;
                const serviceId =
                    park?.QuotingService?.Default ||
                    park?.Service?.Id;
                let selectedTags = Array.isArray(tags) && tags.length
                    ? [...new Set(tags)]
                    : [tag].filter(Boolean);
                if (park?.AlbumKey) {
                    const mediaTags = await dispatch("GetAlbumMetadata", park.AlbumKey);
                    if (mediaTags.length) {
                        selectedTags = [...new Set(mediaTags)];
                    }
                }
                const details = eventDetails || park?.eventDetails || null;
                const isEvent = Boolean(
                    park?.isEvent === true || details?.isEvent === true
                );
                const cartId = uuidv4();

                if (!serviceId) {
                    throw new Error("Quotation service is not configured for this album");
                }

                const albumKeyId = GetAlbumKeyId(park);
                const bookId = reservation
                    ? park?.Reservation?.BookId || null
                    : null;
                const cartKey = BuildCartKey({
                    kind: reservation ? "room-service" : "service",
                    bookId,
                    parkId,
                    serviceId,
                    albumKeyId,
                });

                const item = {
                    id: cartId,
                    CartKey: cartKey,
                    AlbumKeyId: albumKeyId,
                    cantidad: 1,
                    ParkId: parkId,
                    ParkPrefix: helper.methods.getParkPrefix(parkId),
                    uuid: isEvent ? cartId : tag,
                    BookId: bookId,
                    ServiceId: serviceId,
                    type: 1,
                    TotalMedia: park.TotalMedia,
                    IsOnlyHotel: false,
                    tags: selectedTags.map((selectedTag) => ({
                        Tag: selectedTag,
                        VisitDate: park.VisitDate,
                        UdnPrefix: helper.methods.getParkPrefix(parkId),
                        ParkId: parkId,
                    })),
                    park,
                    Id: 0,
                    date: park.VisitDate || moment().format("YYYY-MM-DD"),
                    Amount: 0,
                    NormalIndividual: 0,
                    RateKey: "",
                    isEvent,
                    hasService: false,
                    eventDetails: details,
                };

                if (reservation) {
                    item.Room = park.Reservation.Room;
                }

                const pricedItem = await dispatch("GetNewPrice", item);
                if (!pricedItem?.RateKey || pricedItem?.Id === undefined) {
                    throw new Error("The quotation response is incomplete");
                }
                pricedItem.VisitDate = park.VisitDate;
                pricedItem.CartKey = cartKey;
                pricedItem.AlbumKeyId = albumKeyId;
                pricedItem.park = park;

                commit("setCarrito", pricedItem);
                Vue.set(park, "IsAdded", true);
                Vue.set(park, "uuid", pricedItem.uuid);

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_add_successfully"),
                    type: "success",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } catch (error) {
                albumPromotionCartHelper.RestoreCartQuotationSnapshot({
                    state,
                    snapshot: quotationSnapshot,
                });
                Vue.set(park, "IsAdded", false);
                const cartKey = FindCartKeyByAlbumKey(
                    state.shoppingCart,
                    GetAlbumKeyId(park)
                );
                if (cartKey !== undefined) {
                    commit("REMOVE_CART_ITEM", cartKey);
                }
                await dispatch("SynchronizeAlbumCartState");

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_add"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                dispatch("setLoading", false);
            }
        },
        /**
         * It's adding a park to the shopping cart
         * @param park - This is the park that we're adding to the cart.
         * @returns the value of the `park` object.
         */
        async addCartByTag({ commit, dispatch, state }, park) {
            const quotationSnapshot = albumPromotionCartHelper.CreateCartQuotationSnapshot({
                state,
            });

            try {
                if (IsAlbumCommerciallyExpiredForCart(park)) {
                    Vue.$toast.open({
                        message: i18n.t("gallery.album_expired"),
                        type: "error",
                    });
                    return;
                }

                /* It's calling the action `setLoading` and passing the value `true` to it. */
                dispatch("setLoading", true);

                const parkId = park.Park.Id;
                let cantidad = state.shoppingCart.hasOwnProperty(parkId)
                    ? state.shoppingCart[parkId].cantidad + 1
                    : 1;

                let totalMedia = state.shoppingCart.hasOwnProperty(parkId)
                    ? state.shoppingCart[parkId].TotalMedia
                    : 0;

                let TotalPhotos = totalMedia + park.TotalMedia;

                let item = {
                    id: uuidv4(),
                    cantidad: cantidad,
                    Pax: park.Reservation.Pax,
                    ParkId: parkId,
                    ParkPrefix: helper.methods.getParkPrefix(parkId),
                    type: 1,
                    BookId: null,
                    IsOnlyHotel: false,
                    TotalMedia: TotalPhotos,
                    Id: 0,
                    date: moment().format("YYYY-MM-DD"),
                    Amount: 0,
                    NormalIndividual: 0,
                    RateKey: "",
                };

                //Cuando es Xenotes se toman los Pax que vienen en las propiedad del álbum
                //ToD0: Hay que pulir esta funcionalidad en caso de que otros parques manejen
                //      el mismo esquema.
                if (parkId == 3) {
                    let newPax = park.Reservation.Pax;

                    item.Pax = state.shoppingCart.hasOwnProperty(parkId)
                        ? state.shoppingCart[parkId].Pax + newPax
                        : newPax;
                }

                let p = await dispatch("GetNewPrice", item);

                p.VisitDate = park.VisitDate;
                /* It's setting a property on the park object. */
                Vue.set(park, "IsAdded", true);
                // We obtain all the tags, which are associated with the sale
                let tagsItems = [];
                let tags = Object.values(state.albumes)
                    .filter((item) => item.ParkId == parkId && item.Type == 1)
                    .map(function (item) {
                        item.Tags.map((tag) => {
                            tagsItems.push({
                                Tag: tag,
                                VisitedOn: item.VisitDate,
                                ParkId: parkId,
                            });
                        });
                    });

                p.tags = tagsItems;

                commit("setCarrito", p);

                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                // Actualizamos el componente de Albums
                dispatch("updateAlbumComponent");
                // Actualizamos el componente del carrito
                dispatch("updateShoppingCartComponent");

                Vue.$toast.open({
                    message: i18n.t("messages.album_add_successfully"),
                    type: "success",
                });

                dispatch("setLoading", false);
            } catch (error) {
                albumPromotionCartHelper.RestoreCartQuotationSnapshot({
                    state,
                    snapshot: quotationSnapshot,
                });

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_add"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                dispatch("setLoading", false);
            }
        },
        /**
         * Función para agregar un multiparque de una reserva
         * @param park - El objeto del parque seleccionado
         * @param idx - El índice del álbum en el array de álbumes.
         * @returns regresa el valor del objeto park
         */
        async addCartByRoom({ dispatch, state }, { park, idx }) {
            const quotationSnapshot = albumPromotionCartHelper.CreateCartQuotationSnapshot({
                state,
            });
            let wasQuotedSuccessfully = false;

            try {
                if (IsAlbumCommerciallyExpiredForCart(state.albumes[idx], park)) {
                    Vue.$toast.open({
                        message: i18n.t("gallery.album_expired"),
                        type: "error",
                    });
                    return;
                }

                dispatch("setLoading", true);

                if (HasQuotingService(park.QuotingService)) {
                    let uuid = uuidv4();
                    park.uuid = uuid;

                    await dispatch("addCartByServiceId", {
                        park,
                        tag: uuid,
                        reservation: true,
                    });

                    return;
                }

                albumPromotionCartHelper.MarkAlbumSelection({
                    park,
                    album: state.albumes[idx],
                });

                const regularParks = cartByRoomMix.methods.getOnlyAlbumsParks(idx);

                if (regularParks.length) {
                    await cartByRoomMix.methods.processParks(park, idx);
                }

                await dispatch("syncAlbumPromotionCartItem", {
                    idx,
                    sourceType: 2,
                    triggerPark: park,
                });

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_add_successfully"),
                    type: "success",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
                wasQuotedSuccessfully = true;
            } catch (error) {
                albumPromotionCartHelper.RestoreCartQuotationSnapshot({
                    state,
                    snapshot: quotationSnapshot,
                });

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_add"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                if (wasQuotedSuccessfully) {
                    await helper.methods.checkUpsellUpdate(
                        Object.values(state.shoppingCart)
                    );
                }

                dispatch("setLoading", false);
            }
        },
        /**
         * It's adding event personalized to the shopping cart
         *
         * @param {*} product
         * @returns {JSON} Returns the product assigned in the shopping cart
         */
        async addCartOnlyProduct({ commit, dispatch, state }, product) {
            const quotationSnapshot = albumPromotionCartHelper.CreateCartQuotationSnapshot({
                state,
            });

            try {
                /* It's calling the action `setLoading` and passing the value `true` to it. */
                dispatch("setLoading", true);

                let item = {
                    id: uuidv4(),
                    cantidad: product.cantidad,
                    Pax: product.Pax,
                    ParkId: product.ParkId,
                    ParkPrefix: helper.methods.getParkPrefix(product?.ParkId),
                    type: 1,
                    BookId: null,
                    IsOnlyHotel: false,
                    TotalMedia: product.TotalMedia,
                    Id: 0,
                    date: product.date,
                    Amount: 0,
                    NormalIndividual: 0,
                    RateKey: "",
                    hasService: product.hasService,
                    saleService: product.saleService,
                    isEvent: product.isEvent,
                    eventDetails: product.eventDetails,
                    tags: Array.isArray(product.tags) ? product.tags : [],
                };

                let p = await dispatch("GetNewPrice", item);
                p.VisitDate = product.VisitDate;

                commit("setCarrito", p);

                commit("SET_SHOPPING_CART_AMOUNT", 1);
                await dispatch("getAmount");

                // Actualizamos el componente de Albums
                dispatch("updateAlbumComponent");
                // Actualizamos el componente del carrito
                dispatch("updateShoppingCartComponent");

                dispatch("setLoading", false);

                return p;
            } catch (error) {
                albumPromotionCartHelper.RestoreCartQuotationSnapshot({
                    state,
                    snapshot: quotationSnapshot,
                });

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_add"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                dispatch("setLoading", false);
            }
        },
        /* Getting the amount of items in the shopping cart. */
        async getAmount({ state, commit }) {
            const amount = Object.values(state.shoppingCart).reduce(
                (acc, { Amount }) => acc + Number(Amount || 0),
                0
            );

            commit("SET_AMOUNT", amount);
        },
        /* Getting the amount of items in the shopping cart. */
        async getShoppingCartAmount({ state }) {
            return Object.values(state.shoppingCart).reduce(
                (acc, { cantidad }) => acc + cantidad,
                0
            );
        },
        getEventsInShoppingCart({ state }) {
            let keys = Object.keys(state.shoppingCart);
            if (keys.length > 0) {
                let events = keys.map((item) => {
                    return {
                        key: item,
                        isEvent: state.shoppingCart[item].isEvent,
                    };
                });
                return events;
            }
            return [];
        },
        /* Calling the getShoppingCartAmount method and then committing the result to the
        SET_SHOPPING_CART_AMOUNT mutation. */
        async addShoppingCartAmount({ commit, dispatch }) {
            let amount = await dispatch("getShoppingCartAmount");
            commit("SET_SHOPPING_CART_AMOUNT", amount);
        },
        async removeShoppingCartAmount({ commit, dispatch }) {
            let amount = (await dispatch("getShoppingCartAmount")) - 1;

            // state.shoppingCartAmount
            commit("SET_SHOPPING_CART_AMOUNT", amount);
        },
        AddNewPrice({ commit }, data) {
            commit("setCarrito", data);
        },
        /**
         * Refresh all the cart information;
         */
        async refreshShoppingCart({ commit, dispatch }, withAlert = true) {
            await dispatch("SynchronizeAlbumCartState");
            eventBus.$emit("addShoppingCart", { uuid: uuidv4() });
            await dispatch("addShoppingCartAmount");
            commit("CREATE_NEW_KEY_COMPONENT", uuidv4());
            await dispatch("getAmount");
            if (withAlert) {
                Vue.$toast.open({
                    message: i18n.t("messages.album_remove_successfully"),
                    type: "success",
                });
            }
        },
        /**
         * Funcion para eliminar los eventos del carrito, sin importar el numero de tags que tenga.
         * @EstadoActual : funcion usanda en ShoppingCartComponent
         */
        async removeEventFromShoppingCart({ commit, dispatch }) {
            const events = await dispatch("getEventsInShoppingCart");

            events.forEach((item) => {
                if (item.isEvent) {
                    commit("REMOVE_CART_ITEM", item.key);
                }
            });

            await dispatch("refreshShoppingCart", false);
        },
        /**
         * Funcion para desasociar un parque completo del carrito, sin importar el numero de tags que tenga.
         * @EstadoActual : funcion usanda en ShoppingCartComponent
         */
        async removeEventAllCart({ commit, dispatch }) {
            const events = await dispatch("getEventsInShoppingCart");

            events.forEach((item) => {
                commit("REMOVE_CART_ITEM", item.key);
            });

            await dispatch("refreshShoppingCart", false);
        },
        /**
         * Removes exactly one cart product using its current cart key.
         *
         * Legacy photo products are still keyed by ParkId, while new video and
         * individual-media products use stable composite keys. Album indicators
         * are rebuilt from the remaining cart after the removal.
         *
         * @param {Object} context Vuex action context.
         * @param {string|number} cartKey Shopping-cart key.
         * @returns {Promise<void>}
         */
        async removeParkFromShoppingCart(
            { commit, dispatch },
            cartKey
        ) {
            await dispatch("setLoading", true);

            try {
                commit("REMOVE_CART_ITEM", cartKey);
                await dispatch("refreshShoppingCart");
            } finally {
                dispatch("setLoading", false);
            }
        },
        async removeCodeFromShoppingCart(
            { commit, dispatch, state },
            { park, idx = null, downloadCode = null }
        ) {
            try {
                dispatch("setLoading", true);
                if (idx === null) {
                    idx = Object.values(state.albumes).findIndex(
                        (item) => item.code == downloadCode
                    );
                }

                const code = state.albumes[idx].code;

                if (state.shoppingCart[code].cantidad === 1) {
                    Vue.set(park, "IsAdded", false);
                    commit("REMOVE_CART_ITEM", code);
                } else {
                    Vue.set(park, "IsAdded", false);
                    let album = state.albumes[idx];
                    let parks = album.park.filter((item) => item.IsAdded);
                    const Amount = parks.reduce(
                        (acc, { price }) => acc + price.Amount,
                        0
                    );
                    const cantidad = state.shoppingCart[code].cantidad - 1;
                    let item = {
                        id: uuidv4(),
                        code: code,
                        cantidad: cantidad,
                        Parks: parks,
                        type: 0,
                        Id: 0,
                        date: moment().format("YYYY-MM-DD"),
                        Amount: Amount,
                        NormalIndividual: 0,
                        RateKey: "",
                    };
                    commit("setCarrito", item);
                }
                await dispatch("refreshShoppingCart");

                // Actualizamos el componente de Albums
                dispatch("updateAlbumComponent");
                // Actualizamos el componente del carrito
                dispatch("updateShoppingCartComponent");

                dispatch("setLoading", false);
            } catch (error) {
                dispatch("setLoading", false);

                Vue.set(park, "IsAdded", true);
                Vue.$toast.open({
                    message: i18n.t("messages.album_error_remove"),
                    type: "error",
                });
            }
        },
        async removeBookParkFromShoppingCart(
            { commit, dispatch, state },
            { room, parkIndex, item }
        ) {
            try {
                let pax = item.Pax;
                dispatch("setLoading", true);

                const deletedPark = state.shoppingCart[room].Parks[parkIndex];
                if (state.shoppingCart[room].cantidad === 1) {
                    await dispatch("enableAddItemByBook", {
                        deletedPark: null,
                        room: room,
                    });
                    commit("REMOVE_CART_ITEM", room);
                } else {
                    let hotelCounter = 0; //this should be a boolean
                    let parkCounter = 0;

                    state.shoppingCart[room].Parks.splice(parkIndex, 1);

                    const BookAlbumDate = state.shoppingCart[room]; //To get and validad how many albums still have the book

                    await dispatch("enableAddItemByBook", {
                        deletedPark: deletedPark,
                        room: room,
                    });
                    BookAlbumDate.Parks.filter((bookPark) =>
                        bookPark.Park.IsHotel === 0
                            ? hotelCounter + 1
                            : parkCounter + 1
                    );
                    // Verificamos si en los valores solo llega seleccion de hotel
                    const item = {
                        Room: room,
                        BookId: 0,
                        cantidad: state.shoppingCart[room].cantidad - 1,
                        Pax: pax,
                        ParkId: state.shoppingCart[room].ParkId, //This should be any hotels id (8,11,14(I guess)) and it comes from ShoppingCart
                        ParkPrefix: helper.methods.getParkPrefix(state?.shoppingCart[room]?.ParkId),
                        Parks: state.shoppingCart[room].Parks, //This comes from shoppingCart
                        type: 2,
                        //if by default any park will have false, but if not the hotelcounter wil be evaluate
                        //to validad if it has anyvalue will be a true.
                        IsOnlyHotel: hotelCounter > 0,
                        // TotalMedia: TotalPhotos,
                        Id: 0,
                        Amount: 0,
                        NormalIndividual: 0,
                        RateKey: "",
                    };

                    commit("setCarrito", await dispatch("GetNewPrice", item));
                }
                await dispatch("refreshShoppingCart");
                dispatch("setLoading", false);
            } catch (error) {
                dispatch("setLoading", false);
                Vue.$toast.open({
                    message: i18n.t("messages.album_error_remove"),
                    type: "error",
                });
            }
        },
        async removeParkServiceIdFromShoppingCart(
            { commit, dispatch, state },
            { park, tag }
        ) {
            try {
                dispatch("setLoading", true);

                const cartKey = photoParkHelper.methods.FindQuotingServiceCartKey({
                    shoppingCart: state.shoppingCart,
                    park,
                    tag,
                });

                if (cartKey !== undefined) {
                    commit("REMOVE_CART_ITEM", cartKey);
                }

                Vue.set(park, "IsAdded", false);

                if (park?.uuid) {
                    Vue.delete(park, "uuid");
                }

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_remove_successfully"),
                    type: "success",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } catch (error) {
                Vue.set(park, "IsAdded", true);

                Vue.$toast.open({
                    message: i18n.t("messages.album_error_remove"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                await helper.methods.checkUpsellUpdate(
                    Object.values(state.shoppingCart)
                );

                dispatch("setLoading", false);
            }
        },
        /**
         * Funcion para eliminar albumnes de un parque en el carrito
         * @param {*} album current album data
         * @param {int} albumIndex current album position
         * @EstadoActual : se utiliza en el componente Albumes lines: 287
         * OK
         */
        async removeAlbumFromShoppingCart(
            { commit, dispatch, state },
            { album, albumIndex }
        ) {
            dispatch("setLoading", true);
            album.IsAdded = false;

            const parkId = album.Park.Id;
            const albumTotalMedia = album.TotalMedia;
            const itemCurrentQuantity = state.shoppingCart[parkId].cantidad;

            let item = {
                id: uuidv4(),
                cantidad: itemCurrentQuantity - 1,
                ParkId: parkId,
                ParkPrefix: helper.methods.getParkPrefix(parkId),
                type: 1,
                TotalMedia:
                    state.shoppingCart[parkId].TotalMedia - albumTotalMedia,
                Id: 0,
                Amount: 0,
                NormalIndividual: 0,
                RateKey: "",
            };

            if (parkId == 3) {
                let newPax = album.Reservation.Pax;

                item.Pax = state.shoppingCart.hasOwnProperty(parkId)
                    ? state.shoppingCart[parkId].Pax - newPax
                    : newPax;
            }

            let tagsItems = [];
            let tags = Object.values(state.albumes)
                .filter((item) => item.ParkId == parkId && item.Type == 1)
                .map(function (item) {
                    item.park.map((park) => {
                        if (
                            park.IsAdded &&
                            !HasQuotingService(park.QuotingService)
                        ) {
                            tagsItems.push({
                                Tag: item.code,
                                VisitedOn: item.VisitDate,
                                ParkId: parkId,
                            });
                        }
                    });
                });

            item.tags = tagsItems;

            if (itemCurrentQuantity === 1) {
                commit("REMOVE_CART_ITEM", parkId);
            } else {
                let newPrice = await dispatch("GetNewPrice", item);
                commit("setCarrito", newPrice);
            }

            await dispatch("refreshShoppingCart");
            dispatch("setLoading", false);
        },
        /**
         * Funcion para habilitar el boton de agregar a uno o mas item de un algum de hotel
         * @param {*} deletedPark
         *
         */
        async enableAddItemByBook({ state }, { deletedPark, room }) {
            let currentAlbumIndex;
            state.albumes.filter((album, albumIndex) => {
                if (
                    parseInt(album.code) === room &&
                    parseInt(album.Type) === 2
                ) {
                    currentAlbumIndex = albumIndex;
                    album.park.filter((pk) => {
                        if (deletedPark) {
                            if (pk.AlbumKey.Id === deletedPark.AlbumKey.Id) {
                                pk.IsAdded = false;
                            }
                        } else {
                            pk.IsAdded = false;
                        }
                    });
                }
            });
            /**
             * todo: this is uggly, this should be refactor
             */
            if (!deletedPark) {
                //why this should have a free hotels in 0
                state.albumes[currentAlbumIndex].FreeHotels = 0;
            }
        },
        /**
         * Funcion para habilitar el boton de agregar a uno o mas item de un algum de hotel
         * @param {*} deletedPark
         *
         */
        async enableAddItemByCode({ state }, { deletedPark, code }) {
            let currentAlbumIndex;
            state.albumes.filter((album, albumIndex) => {
                if (album.code === code && parseInt(album.Type) === 0) {
                    currentAlbumIndex = albumIndex;
                    album.park.filter((pk) => {
                        if (deletedPark) {
                            if (pk.AlbumKey.Id === deletedPark.AlbumKey.Id) {
                                pk.IsAdded = false;
                            }
                        } else {
                            pk.IsAdded = false;
                        }
                    });
                }
            });
            /**
             * todo: this is uggly, this should be refactor
             */
            if (!deletedPark) {
            }
        },
        async removeShoppingCartByRoom(
            { dispatch, state },
            { park, idx, room = null }
        ) {
            try {
                dispatch("setLoading", true);

                if (park.WasAutoAddedByPromotion || park.WasIncludedByPromotion) {
                    Vue.$toast.open({
                        message: i18n.t("messages.album_included_cannot_remove"),
                        type: "info",
                    });

                    return;
                }

                if (room != null) {
                    idx = state.albumes.findIndex(
                        (item) => item.code.toString() == room.toString()
                    );
                }

                if (idx < 0 || !state.albumes[idx]) {
                    return;
                }

                const album = state.albumes[idx];
                const cartKey = parseInt(park?.Reservation?.BookId);
                const currentCartItem = state.shoppingCart[cartKey] || null;

                albumPromotionCartHelper.RemoveIncludedAlbumsFromRemovedBlock({
                    album,
                    park,
                    currentCartItem,
                });

                Vue.set(park, "IsAdded", false);
                photoParkHelper.methods.ClearPromotionFlags(park);
                albumPromotionCartHelper.NormalizeAlbumPromotionBlocks({
                    album,
                    currentCartItem,
                });

                await dispatch("syncAlbumPromotionCartItem", {
                    idx,
                    sourceType: 2,
                    triggerPark: park,
                });

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_remove_successfully"),
                    type: "success",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } catch (error) {
                Vue.$toast.open({
                    message: i18n.t("messages.album_error_remove"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                await helper.methods.checkUpsellUpdate(
                    Object.values(state.shoppingCart)
                );

                dispatch("setLoading", false);
            }
        },

        async removeShoppingCartByDownloadCode(
            { dispatch, state },
            { park, idx, code = null }
        ) {
            try {
                dispatch("setLoading", true);

                if (park.WasAutoAddedByPromotion || park.WasIncludedByPromotion) {
                    Vue.$toast.open({
                        message: i18n.t("messages.album_included_cannot_remove"),
                        type: "info",
                    });

                    return;
                }

                if (code != null) {
                    idx = state.albumes.findIndex(
                        (item) => item.code.toString() == code.toString()
                    );
                }

                if (idx < 0 || !state.albumes[idx]) {
                    return;
                }

                const album = state.albumes[idx];
                const cartKey = code != null ? code : album.code;
                const currentCartItem = state.shoppingCart[cartKey] || null;

                albumPromotionCartHelper.RemoveIncludedAlbumsFromRemovedBlock({
                    album,
                    park,
                    currentCartItem,
                    excludeUnlocked: true,
                });

                Vue.set(park, "IsAdded", false);
                photoParkHelper.methods.ClearPromotionFlags(park);
                albumPromotionCartHelper.NormalizeAlbumPromotionBlocks({
                    album,
                    currentCartItem,
                    excludeUnlocked: true,
                });

                await dispatch("syncAlbumPromotionCartItem", {
                    idx,
                    sourceType: 0,
                    triggerPark: park,
                });

                await dispatch("SynchronizeAlbumCartState");
                await dispatch("addShoppingCartAmount");
                await dispatch("getAmount");

                Vue.$toast.open({
                    message: i18n.t("messages.album_remove_successfully"),
                    type: "success",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } catch (error) {
                Vue.$toast.open({
                    message: i18n.t("messages.album_error_remove"),
                    type: "error",
                });

                dispatch("updateAlbumComponent");
                dispatch("updateShoppingCartComponent");
            } finally {
                await helper.methods.checkUpsellUpdate(
                    Object.values(state.shoppingCart)
                );

                dispatch("setLoading", false);
            }
        },
        async removeItemShoppingCart({ commit, dispatch }, data) {
            commit("SET_REMOVE_ITEM_SHOPPING_CART", data);
            await dispatch("SynchronizeAlbumCartState");
            dispatch("addShoppingCartAmount");
            // Actualizamos el componente de Albums
            dispatch("updateAlbumComponent");
            dispatch("getAmount");
        },
        async removeItemByTagCart(
            { commit, dispatch },
            { visitDate, parkId, serviceId = null }
        ) {
            commit("REMOVE_ITEM_BY_TAG_CART", { visitDate, parkId, serviceId });
            await dispatch("SynchronizeAlbumCartState");
            dispatch("addShoppingCartAmount");
            // Actualizamos el componente de Albums
            dispatch("updateAlbumComponent");
            dispatch("getAmount");
        },
        updateAlbum({ commit, dispatch }, data) {
            commit("SET_UPDATE_ALBUM_PV", data);
        },
        clearShoppingCart({ commit }) {
            commit("SET_CLEAR_ALBUMES");
        },
        deleteDownloadCodesInAlbums({ commit }) {
            commit("SET_DELETE_DOWNLOAD_CODES_IN_ALBUMS");
        },
        cleanAfterShopping({ commit, state }) {
            // verificamos si existe algun Tag en el carrito
            const hasTagsinShoppingCart = Object.values(
                state.shoppingCart
            ).some((item) => item.type === 1);
            // verificamos si existe algun Tag en el carrito
            // const hasTagsinShoppingCart = Object.values(state.shoppingCart).some(item => item.type === 1);

            if (hasTagsinShoppingCart) {
                commit("SET_REMOVE_TAGS_PURCHASED_ALBUMS");
            }

            // Limpiamos el Carrito
            commit("SET_CLEAR_SHOPPING_CART");
            // Habilitamos el TAB de Codigos de descarga
            state.tabDownloadCode = true;
            // Seleccionamos el tab de Codigo de descarga como preterminado
            state.typeCodeTab = 0;
        },
        AddParks({ commit }, data) {
            commit("SET_PARKS", data);
        },
        AddEventDetails({ commit }, data) {
            commit("SET_EVENT_DETAILS", data);
        },
        SetBanks({ commit }, data) {
            commit("SET_BANKS", data);
        },
        SetSafetyPayBanks({ commit }, data) {
            commit("SET_SAFETY_PAY_BANKS", data);
        },
    },
    getters: {
        getLanguage(state) {
            return state.Language;
        },
        updateAlbumComponent(state) {
            return state.componentes.album;
        },
        uuidShoppingCartComponent(state) {
            return state.componentes.shoppingCart;
        },
        getIPAddress(state) {
            return state.IPAddress;
        },
        getLoading(state) {
            return state.loading;
        },
        userLoggedIn(state) {
            return state.userLoggedIn;
        },
        isGuest(state) {
            return state.isGuest;
        },
        Codes(state) {
            return state.codes;
        },
        GetParks(state) {
            return state.Parks;
        },
        Banks(state) {
            return state.Banks;
        },
        SafetyPayBanks(state) {
            return state.SafetyPayBanks;
        },
        Currency(state) {
            return state.Currency;
        },
        CurrentPrice(state) {
            return state.CurrentPrice;
        },
        shoppingCart(state) {
            return state.shoppingCart;
        },
        getCodesType(state) {
            const albumes = Object.values(state.albumes);

            let types = albumes.reduce(function (albumes, album) {
                if (album in albumes) {
                    albumes[album.Type]++;
                } else {
                    albumes[album.Type] = 1;
                }

                return albumes;
            }, {});

            let countTypes = Object.values(types).length;
            let banner =
                countTypes == 1
                    ? parseInt(Object.keys(types).shift())
                    : "default";

            return banner;
        },
        shoppingCartCounter(state) {
            return Object.values(state.shoppingCart).reduce(
                (acc, { cantidad }) => acc + cantidad,
                0
            );
        },
        Albumes(state) {
            return state.albumes;
        },
        needReloadAlbumes(state) {
            return state.needReloadAlbumes;
        },
        SpinningMultimedia(state) {
            return state.downloaded.spinning;
        },
        ProgressingMultimedia(state) {
            return state.downloaded.progressing;
        },
        CompressingMultimedia(state) {
            return state.downloaded.compressing;
        },
        downloaded(state) {
            return state.downloaded.data;
        },
        downloadedProgress(state) {
            return state.downloaded.progress;
        },
        CompressingZipCompressed(state) {
            return state.downloaded.compressingZip.compressed;
        },
        compressingZipTotal(state) {
            return state.downloaded.compressingZip.total;
        },
        CompletedDownload(state) {
            return state.downloaded.completed;
        },
        DownloadsBoxStatus(state) {
            return state.componentes.displayDownloadBox;
        },
        Contries(state) {
            return state.payment.countries;
        },
        CountryStates(state) {
            return state.payment.states;
        },
        getCurrency(state) {
            return state.Currency;
        },
        Amount(state) {
            return Object.values(state.shoppingCart).reduce(
                (acc, { cantidad, Amount }) => acc + Amount,
                0
            );
        },
        PaxWebsite(state) {
            return state.payment.pax.website;
        },
        existingSaleShoppingCart(state) {
            return Object.values(state.shoppingCart).some(
                (item) => item.type === 0
            );
        },
        // Validamos si presentar la opcion de pago con PayPal
        hasPayPal(state) {
            return state.CardTypes.some((el) => el.cardTypeCode === "PP");
        },
        getPackages(state) {
            return state.albumes
                .filter((x) => x.Type == 0)
                .map((item) => item.getCode.PackageName);
        },
        hasAlbumsReady(state) {
            return state.albumes.length;
        },
        someAlbumDownloadCode(state) {
            return Object.values(state.albumes).some((item) => item.Type);
        },
        authState(state) {
            return state.userLoggedIn || state.isGuest;
        },
        authStateLogged(state) {
            return state.userLoggedIn && !state.isGuest;
        },
        authStateGuest(state) {
            return state.userLoggedIn && state.isGuest;
        },
    },
});
